import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from "recharts";
import {
  FiUsers,
  FiTrendingUp,
  FiAward,
  FiAlertCircle,
  FiBook,
  FiBarChart2,
  FiPieChart,
  FiClock,
  FiTarget,
  FiCheckCircle,
  FiXCircle,
  FiMinusCircle,
  FiArrowLeft
} from 'react-icons/fi';

const AdminDashboard = () => {
  const { id } = useParams();
  const location = useLocation();
  const paperName = location.state?.paperName || "Paper Overview";
  const navigate = useNavigate();
  const [apiData, setApiData] = useState(null);
  const [marksDistributionData, setMarksDistributionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marksLoading, setMarksLoading] = useState(true);

  // Modal states
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedRangeStudents, setSelectedRangeStudents] = useState([]);
  const [selectedRangeInfo, setSelectedRangeInfo] = useState({ range: '', percentage: '' });
  const [topStudents, setTopStudents] = useState([]);
  const [poorStudents, setPoorStudents] = useState([]);
  const [activeTab, setActiveTab] = useState('top');
  const [statsSummary, setStatsSummary] = useState({
    totalStudents: 0,
    avgScore: 0,
    completionRate: 0,
    avgTime: 0
  });

  const [papers, setPapers] = useState([]);
  const [selectedPaperId, setSelectedPaperId] = useState("");

  // Open student modal
  const openStudentModal = (students, range, percentage) => {
    setSelectedRangeStudents(students || []);
    setSelectedRangeInfo({ range, percentage });
    setShowStudentModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Close student modal
  const closeStudentModal = () => {
    setShowStudentModal(false);
    document.body.style.overflow = 'unset';
  };

  async function fetchData() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "overviewOfTheExam";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);

      const response = await api.post("overView/overView2", fd);

      if (response.data && response.data.data) {
        const raw = response.data.data;

        const normalizedData = {
          overallStats: raw.overallStatistics || {},
          subjectWiseStats: raw.subjectStatistics || [],
          chapterStats: raw.chapterStatistics || [],
          topicStats: raw.topicStatistics || [],
        };

        setApiData(normalizedData);

        setStatsSummary({
          totalStudents: raw.overallStatistics?.totalStudents || 0,
          avgScore: raw.overallStatistics?.correctPercentage || 0,
          completionRate: Number(
            (100 - (raw.overallStatistics?.unansweredPercentage || 0)).toFixed(1)
          ),
          avgTime: 45
        });
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching:", error);
      setLoading(false);
    }
  }

  async function fetchPapers() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getPaperDetailsForDashboard";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("limit", 1000);

      const response = await api.post("overView/overView", fd);

      if (response.data?.status && response.data?.data?.length > 0) {
        setPapers(response.data.data);
        setSelectedPaperId(response.data.data[0].id);
      }

    } catch (error) {
      console.error("Error fetching papers:", error);
    }
  }

  async function fetchPaperDetails() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getPaperDetailsForDashboard";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);

      const response = await api.post("overView/overView2", fd);
      // console.log("Papers details Data:", response);
    } catch (error) {
      console.error("Error fetching papers:", error);
    }
  }

  async function topper() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", "getTopAndWeakStudentsForPaper");
      fd.append("paperId", id);

      const response = await api.post("overView/overView2", fd);
      const paperResult = response.data?.paperWiseResults?.[0];

      if (paperResult) {
        setTopStudents(paperResult.topStudents || []);
        setPoorStudents(paperResult.weakStudents || []);
      } else {
        setTopStudents([]);
        setPoorStudents([]);
      }
    } catch (error) {
      console.error("Error fetching topper data:", error);
    }
  }

  async function getMarks() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getMarksDistribution";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);

      const response = await api.post("overView/overView2", fd);
      if (response.data && response.data.data) {
        setMarksDistributionData(response.data.data);
      }
      setMarksLoading(false);
    } catch (error) {
      console.error("Error fetching marks distribution:", error);
      setMarksLoading(false);
    }
  }

  useEffect(() => {
    fetchPapers();
    fetchPaperDetails();
  }, []);

  useEffect(() => {
    if (selectedPaperId) {
      fetchData();
      topper();
      getMarks();
    }
  }, [selectedPaperId]);

  const generateTopVsPoorGraphData = () => {
    const maxLength = Math.max(topStudents.length, poorStudents.length);

    return Array.from({ length: maxLength }).map((_, index) => ({
      label: `#${index + 1}`,
      top: topStudents[index]
        ? Number(topStudents[index].accuracyPercentage)
        : 0,
      poor: poorStudents[index]
        ? Number(poorStudents[index].accuracyPercentage)
        : 0,
    }));
  };

  const generateSubjectDistribution = () => {
    if (!apiData?.subjectWiseStats) return [];
    return apiData.subjectWiseStats.map(subject => ({
      name: subject.subjectName,
      value: subject.accuracy || 0
    }));
  };

  const generateDifficultyAnalysis = () => {
    return [
      { level: 'Easy', questions: 15, correct: 85 },
      { level: 'Medium', questions: 25, correct: 65 },
      { level: 'Hard', questions: 10, correct: 35 }
    ];
  };

   if (loading) {
  return (
    <div className='dashboard-container'
      style={{
        position: "fixed",
        inset: 0,
   
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: "2.5rem 3rem",
          borderRadius: "1.25rem",
          textAlign: "center",
          boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
          border: "1px solid #e5e7eb",
          width: "90%",
          maxWidth: "360px",
          marginLeft:"250px"
        }}
      >
        {/* Spinner */}
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #3b82f6",
            margin: "0 auto",
            animation: "spin 1s linear infinite",
          }}
        />

        <h3
          style={{
            marginTop: "1rem",
            marginBottom: "0.25rem",
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "#1f2937",
          }}
        >
          Loading 
        </h3>

        <p
          style={{
            fontSize: "0.875rem",
            color: "#64748b",
            marginBottom: "1.5rem",
          }}
        >
          Fetching analytics & insights…
        </p>

        {/* Skeleton */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {["80%", "60%", "90%"].map((w, i) => (
            <div
              key={i}
              style={{
                height: "10px",
                width: w,
                margin: "0 auto",
                borderRadius: "999px",
                background:
                  "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%)",
                backgroundSize: "400% 100%",
                animation: "shimmer 1.4s ease infinite",
              }}
            />
          ))}
        </div>

        {/* Inline keyframes */}
        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            @keyframes shimmer {
              0% { background-position: 100% 0; }
              100% { background-position: -100% 0; }
            }
          `}
        </style>
      </div>
    </div>
  );
}

  if (!apiData) {
    return (
      <div className="dashboard-container">
        <div className="error-container">
          <FiAlertCircle size={48} color="#ef4444" />
          <h3>Error Loading Dashboard</h3>
          <p>Please try again later</p>
        </div>
      </div>
    );
  }

  const topVsPoorData = generateTopVsPoorGraphData();
  const subjectData = generateSubjectDistribution();
  const difficultyData = generateDifficultyAnalysis();
  const overallStats = apiData.overallStats || {};

  const summaryCards = [
    {
      title: "Total Students",
      value: statsSummary.totalStudents,
      icon: <FiUsers size={20} />,
      color: "text-blue-600",
      iconBg: "bg-blue-100"
    },
    {
      title: "Average Score",
      value: `${statsSummary.avgScore}%`,
      icon: <FiTrendingUp size={20} />,
      color: "text-green-600",
      iconBg: "bg-green-100"
    },
    {
      title: "Completion Rate",
      value: `${statsSummary.completionRate}%`,
      icon: <FiAward size={20} />,
      color: "text-purple-600",
      iconBg: "bg-purple-100"
    },
    {
      title: "Avg Time Taken",
      value: `${statsSummary.avgTime} min`,
      icon: <FiClock size={20} />,
      color: "text-amber-600",
      iconBg: "bg-amber-100"
    }
  ];

  const performanceStats = [
    {
      label: "Correct",
      value: `${overallStats.correctPercentage || 0}%`,
      icon: <FiCheckCircle size={16} />,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      label: "Wrong",
      value: `${overallStats.wrongPercentage || 0}%`,
      icon: <FiXCircle size={16} />,
      color: "text-red-600",
      bgColor: "bg-red-50"
    },
    {
      label: "Unanswered",
      value: `${overallStats.unansweredPercentage || 0}%`,
      icon: <FiMinusCircle size={16} />,
      color: "text-gray-600",
      bgColor: "bg-gray-50"
    }
  ];

  return (
    <div className="dashboard-container">
      {/* Header */}
     

      <div className="dashboard-header">
        <div className="header-content">
          <h1>Exam Overview</h1>
          <p>{paperName} • Paper Analysis & Performance Metrics</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        {summaryCards.map((card, index) => (
          <div key={index} className="summary-card">
            <div className={`summary-icon ${card.iconBg}`}>
              {card.icon}
            </div>
            <div className="summary-content">
              <div className="summary-title">{card.title}</div>
              <div className={`summary-value ${card.color}`}>{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Overview */}
      <div className="performance-overview-card">
        <div className="card-header">
          <h2><FiBarChart2 /> Performance Overview</h2>
        </div>
        <div className="performance-stats">
          {performanceStats.map((stat, index) => (
            <div key={index} className={`performance-stat ${stat.bgColor}`}>
              <div className="stat-icon" style={{ color: stat.color.replace('text-', '') }}>
                {stat.icon}
              </div>
              <div>
                <div className="stat-label">{stat.label}</div>
                <div className={`stat-value ${stat.color}`}>{stat.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="content-grid">
        {/* Left Column */}
        <div className="left-column">
          {/* Top vs Poor Performance Chart */}
          <div className="chart-card">
            <div className="card-header">
              <h2><FiTrendingUp /> Top vs Low Performers</h2>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={topVsPoorData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" domain={[0, 100]} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="top"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.25}
                    name="Top Performers (%)"
                  />
                  <Area
                    type="monotone"
                    dataKey="poor"
                    stroke="#ef4444"
                    fill="#ef4444"
                    fillOpacity={0.25}
                    name="Low Performers (%)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Marks Distribution */}
          <div className="marks-distribution-card">
            <div className="card-header">
              <h2><FiPieChart /> Marks Distribution</h2>
            </div>
            <div className="marks-content">
              {marksLoading ? (
                <div className="loading-small">Loading distribution...</div>
              ) : marksDistributionData?.marksDistribution?.length > 0 ? (
                <>
                  <table className="marks-table">
                    <thead>
                      <tr>
                        <th>Marks Range</th>
                        <th>Students</th>
                        <th>Distribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marksDistributionData.marksDistribution.map((item, idx) => (
                        <tr key={idx}>
                          <td className="range-cell">{item.range}</td>
                          <td 
                            className="students-cell clickable"
                            onClick={() => openStudentModal(item.students || [], item.range, item.percentage)}
                          >
                            {item.studentCount}
                          </td>
                          <td className="distribution-cell">
                            <div className="distribution-info">
                              <span>{item.percentage}%</span>
                              <div className="distribution-bar">
                                <div 
                                  className="distribution-fill"
                                  style={{ width: `${item.percentage}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {marksDistributionData.statistics && (
                    <div className="marks-stats">
                      <div className="stat-item">
                        <span>Average Marks</span>
                        <strong>{marksDistributionData.statistics.averageMarks}</strong>
                      </div>
                      <div className="stat-item">
                        <span>Highest Marks</span>
                        <strong>{marksDistributionData.statistics.highestMarks}</strong>
                      </div>
                      <div className="stat-item">
                        <span>Lowest Marks</span>
                        <strong>{marksDistributionData.statistics.lowestMarks}</strong>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">No marks distribution data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="right-column">
          {/* Student Performance */}
          <div className="student-performance-card">
            <div className="card-header">
              <h2><FiAward /> Student Performance</h2>
              <div className="tab-buttons">
                <button 
                  className={`tab-button ${activeTab === 'top' ? 'active' : ''}`}
                  onClick={() => setActiveTab('top')}
                >
                  Top Performers
                </button>
                <button 
                  className={`tab-button ${activeTab === 'weak' ? 'active' : ''}`}
                  onClick={() => setActiveTab('weak')}
                >
                  Weak Performers
                </button>
              </div>
            </div>
            <div className="performance-table-container">
                <div className="table-responsive-wrapper">

              {activeTab === 'top' ? (
                topStudents.length > 0 ? (
                  <table className="performance-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Student Name</th>
                        <th>Accuracy</th>
                        <th>Marks</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topStudents.map((student, index) => (
                        <tr key={index}>
                          <td>
                            <div className="rank-badge top-rank">{index + 1}</div>
                          </td>
                          <td className="student-name">{student.name}</td>
                          <td>
                            <div className="accuracy-badge top-accuracy">
                              {student.accuracyPercentage}%
                            </div>
                          </td>
                          <td className="marks-display">{student.totalMarks}</td>
                          <td>
                            <button 
                              className="view-button"
                              onClick={() => navigate(`/student-overall/${student.studentId}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <FiAward size={24} />
                    <p>No top performers data available</p>
                  </div>
                )
              ) : (
                poorStudents.length > 0 ? (
                  <table className="performance-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Student Name</th>
                        <th>Accuracy</th>
                        <th>Marks</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poorStudents.map((student, index) => (
                        <tr key={index}>
                          <td>
                            <div className="rank-badge weak-rank">{index + 1}</div>
                          </td>
                          <td className="student-name">{student.name}</td>
                          <td>
                            <div className="accuracy-badge weak-accuracy">
                              {student.accuracyPercentage}%
                            </div>
                          </td>
                          <td className="marks-display weak-marks">{student.totalMarks}</td>
                          <td>
                            <button 
                              className="view-button"
                              onClick={() => navigate(`/student-overall/${student.studentId}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <FiAlertCircle size={24} />
                    <p>No weak performers data available</p>
                  </div>
                )
              )}
              </div>
            </div>
          </div>

          {/* Subject Accuracy Chart */}
          <div className="subject-accuracy-card">
            <div className="card-header">
              <h2>📚 Subject Accuracy</h2>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={subjectData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Difficulty Analysis */}
          <div className="difficulty-card">
            <div className="card-header">
              <h2><FiTarget /> Difficulty Analysis</h2>
            </div>
            <div className="difficulty-content">
              {difficultyData.map((item, idx) => (
                <div key={idx} className="difficulty-item">
                  <div className="difficulty-header">
                    <span className="difficulty-level">{item.level}</span>
                    <span className="difficulty-percent">{item.correct}%</span>
                  </div>
                  <div className="difficulty-bar">
                    <div
                      className="difficulty-fill"
                      style={{
                        width: `${item.correct}%`,
                        backgroundColor: idx === 0 ? '#10b981' : idx === 1 ? '#f59e0b' : '#ef4444'
                      }}
                    />
                  </div>
                  <div className="difficulty-meta">
                    <span>{item.questions} questions</span>
                    <span>{item.correct}% correct</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Time Analysis Card */}
      <div className="time-analysis-card">
        <div className="card-header">
          <h2>⏱️ Time Analysis</h2>
        </div>
        <div className="time-stats-grid">
          <div className="time-stat">
            <div className="time-icon">
              <FiClock size={20} />
            </div>
            <div className="time-info">
              <div className="time-label">Fastest Completion</div>
              <div className="time-value">28 min</div>
            </div>
          </div>
          <div className="time-stat">
            <div className="time-icon">
              <FiClock size={20} />
            </div>
            <div className="time-info">
              <div className="time-label">Average Time</div>
              <div className="time-value">45 min</div>
            </div>
          </div>
          <div className="time-stat">
            <div className="time-icon">
              <FiClock size={20} />
            </div>
            <div className="time-info">
              <div className="time-label">Slowest Completion</div>
              <div className="time-value">78 min</div>
            </div>
          </div>
          <div className="time-stat">
            <div className="time-icon">
              <FiTarget size={20} />
            </div>
            <div className="time-info">
              <div className="time-label">Optimal Time</div>
              <div className="time-value">40 min</div>
            </div>
          </div>
        </div>
      </div>

      {/* Student Details Modal */}
      {showStudentModal && (
        <div className="modal-overlay" onClick={closeStudentModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                👨‍🎓 Students: {selectedRangeInfo.range} ({selectedRangeInfo.percentage}%)
                <span className="student-count">
                  {selectedRangeStudents.length} student{selectedRangeStudents.length !== 1 ? 's' : ''}
                </span>
              </h3>
              <button className="modal-close" onClick={closeStudentModal}>×</button>
            </div>
            <div className="modal-content">
              {selectedRangeStudents.length > 0 ? (
                <div className="modal-table-container">
                  <table className="modal-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Student Name</th>
                        <th>Student ID</th>
                        <th>Correct</th>
                        <th>Wrong</th>
                        <th>Marks</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRangeStudents.map((student, idx) => (
                        <tr key={idx}>
                          <td className="modal-index">{idx + 1}</td>
                          <td className="modal-name">{student.studentName}</td>
                          <td className="modal-id">{student.studentId}</td>
                          <td className="modal-correct">{student.correctAnswers}</td>
                          <td className="modal-wrong">{student.wrongAnswers}</td>
                          <td className="modal-marks">{student.obtainedMarks}</td>
                          <td>
                            <button 
                              className="modal-view-button"
                              onClick={() => {
                                document.body.style.overflow = 'unset';
                                navigate(`/student-overall/${student.studentId}`);
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">No students found in this range</div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .dashboard-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%);
            padding: 1.5rem;
          }

          /* Loading States */
          .loading-container, .error-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
          }

          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e0f2fe;
            border-top: 3px solid #0369a1;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          .loading-text {
            margin-top: 12px;
            color: #64748b;
            font-size: 0.875rem;
          }

          .error-container h3 {
            color: #ef4444;
            margin: 16px 0 8px;
          }

          .error-container p {
            color: #6b7280;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }



          /* Header */
          .dashboard-header {
         
            margin: 0 auto 2rem;
            background: white;
            border-radius: 1rem;
            padding: 1.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            border: 1px solid #e2e8f0;
          }

          .header-content h1 {
            font-size: 1.75rem;
            font-weight: 700;
            color: #1f2937;
            margin: 0 0 0.5rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .header-content p {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 0;
          }

          /* Summary Cards */
          .summary-grid {
        
            margin: 0 auto 1.5rem;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1rem;
          }

          .summary-card {
            background: white;
            border-radius: 1rem;
            padding: 1.25rem;
            display: flex;
            align-items: center;
            gap: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            transition: all 0.3s;
          }

          .summary-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
            border-color: #cbd5e0;
          }

          .summary-icon {
            width: 3rem;
            height: 3rem;
            border-radius: 0.75rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .summary-content {
            flex: 1;
          }

          .summary-title {
            font-size: 0.75rem;
            color: #6b7280;
            font-weight: 500;
            margin-bottom: 0.25rem;
          }

          .summary-value {
            font-size: 1.5rem;
            font-weight: 700;
          }

          /* Performance Overview */
          .performance-overview-card {
            
            margin: 0 auto 1.5rem;
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            overflow: hidden;
          }

          .card-header {
            padding: 1rem 1.5rem;
            border-bottom: 1px solid #f3f4f6;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .card-header h2 {
            font-size: 1rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .performance-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            padding: 1.5rem;
            gap: 1rem;
          }

          .performance-stat {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1rem;
            border-radius: 0.75rem;
          }

          .stat-icon {
            width: 2.5rem;
            height: 2.5rem;
            border-radius: 0.625rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .stat-label {
            font-size: 0.75rem;
            color: #6b7280;
            font-weight: 500;
            margin-bottom: 0.25rem;
          }

          .stat-value {
            font-size: 1.25rem;
            font-weight: 700;
          }

          /* Content Grid */
          .content-grid {
          
            margin: 0 auto 1.5rem;
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 1.5rem;
          }

          /* Chart Cards */
          .chart-card, .marks-distribution-card, .student-performance-card,
          .subject-accuracy-card, .difficulty-card {
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            overflow: hidden;
            margin-bottom: 1.5rem;
          }

          .chart-container {
            padding: 1.5rem;
          }

          /* Marks Distribution */
          .marks-content {
            padding: 1.5rem;
          }

          .marks-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.875rem;
            margin-bottom: 1.5rem;
          }

          .marks-table thead {
            background: #f9fafb;
          }

          .marks-table th {
            padding: 0.75rem 1rem;
            text-align: left;
            font-weight: 600;
            color: #374151;
            border-bottom: 1px solid #e5e7eb;
          }

          .marks-table td {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid #f3f4f6;
          }

          .range-cell {
            font-weight: 500;
            color: #1f2937;
          }

          .students-cell.clickable {
            color: #3b82f6;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .students-cell.clickable:hover {
            background: #eff6ff;
          }

          .distribution-info {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .distribution-bar {
            flex: 1;
            height: 0.5rem;
            background: #e5e7eb;
            border-radius: 0.25rem;
            overflow: hidden;
          }

          .distribution-fill {
            height: 100%;
            background: #3b82f6;
            border-radius: 0.25rem;
            transition: width 1s ease-in-out;
          }

          .marks-stats {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
            padding: 1rem;
            background: #f9fafb;
            border-radius: 0.75rem;
            border: 1px solid #e5e7eb;
          }

          .stat-item {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .stat-item span {
            font-size: 0.75rem;
            color: #6b7280;
          }

          .stat-item strong {
            font-size: 1rem;
            font-weight: 700;
            color: #1f2937;
          }

          /* Student Performance */
          .tab-buttons {
            display: flex;
            gap: 0.5rem;
          }

          .tab-button {
            padding: 0.5rem 1rem;
            border: 1px solid #e5e7eb;
            background: white;
            border-radius: 0.5rem;
            font-size: 0.75rem;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.2s;
          }

          .tab-button.active {
            background: #3b82f6;
            color: white;
            border-color: #3b82f6;
          }

          .performance-table-container {
            padding: 1.5rem;
          }

          .performance-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.875rem;
          }

          .performance-table thead {
            background: #f9fafb;
          }

          .performance-table th {
            padding: 0.75rem 1rem;
            text-align: left;
            font-weight: 600;
            color: #374151;
            border-bottom: 2px solid #e5e7eb;
          }

          .performance-table td {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid #f3f4f6;
          }

          .performance-table tbody tr:hover {
            background: #f9fafb;
          }

          .rank-badge {
            width: 1.75rem;
            height: 1.75rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 0.75rem;
          }

          .top-rank {
            background: #3b82f6;
            color: white;
          }

          .weak-rank {
            background: #ef4444;
            color: white;
          }

          .student-name {
            font-weight: 500;
            color: #1f2937;
          }

          .table-responsive-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

          .accuracy-badge {
            display: inline-block;
            padding: 0.25rem 0.5rem;
            border-radius: 0.75rem;
            font-size: 0.75rem;
            font-weight: 600;
            min-width: 4rem;
            text-align: center;
          }

          .top-accuracy {
            background: #d1fae5;
            color: #065f46;
          }

          .weak-accuracy {
            background: #fee2e2;
            color: #991b1b;
          }

          .marks-display {
            font-weight: 600;
            color: #1f2937;
          }

          .weak-marks {
            color: #ef4444;
          }

          .view-button {
            padding: 0.375rem 0.75rem;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.75rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .view-button:hover {
            background: #2563eb;
            transform: translateY(-1px);
          }

          /* Subject Accuracy */
          .subject-accuracy-card .chart-container {
            height: 200px;
          }

          /* Difficulty Analysis */
          .difficulty-content {
            padding: 1.5rem;
          }

          .difficulty-item {
            margin-bottom: 1.25rem;
          }

          .difficulty-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.5rem;
          }

          .difficulty-level {
            font-weight: 600;
            color: #374151;
            font-size: 0.875rem;
          }

          .difficulty-percent {
            font-weight: 700;
            color: #1f2937;
            font-size: 0.875rem;
          }

          .difficulty-bar {
            height: 0.375rem;
            background: #e5e7eb;
            border-radius: 0.1875rem;
            overflow: hidden;
            margin-bottom: 0.5rem;
          }

          .difficulty-fill {
            height: 100%;
            border-radius: 0.1875rem;
          }

          .difficulty-meta {
            display: flex;
            justify-content: space-between;
            font-size: 0.75rem;
            color: #6b7280;
          }

          /* Time Analysis */
          .time-analysis-card {
    
            margin: 0 auto;
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            overflow: hidden;
          }

          .time-stats-grid {
            padding: 1.5rem;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }

          .time-stat {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1rem;
            background: #f9fafb;
            border-radius: 0.75rem;
          }

          .time-icon {
            width: 2.5rem;
            height: 2.5rem;
            background: white;
            border-radius: 0.625rem;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #3b82f6;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }

          .time-info {
            flex: 1;
          }

          .time-label {
            font-size: 0.75rem;
            color: #6b7280;
            margin-bottom: 0.25rem;
          }

          .time-value {
            font-size: 1.125rem;
            font-weight: 700;
            color: #1f2937;
          }

          /* Empty States */
          .empty-state {
            text-align: center;
            padding: 3rem 2rem;
            color: #6b7280;
            font-size: 0.875rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.75rem;
          }

          .loading-small {
            text-align: center;
            padding: 2rem;
            color: #6b7280;
            font-size: 0.875rem;
          }

          /* Modal Styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            animation: fadeIn 0.3s ease;
            backdrop-filter: blur(3px);
          }

          .modal-container {
            background: white;
            border-radius: 1rem;
            width: 90%;
            max-width: 800px;
            max-height: 80vh;
            overflow: hidden;
            box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
            animation: modalSlideUp 0.4s ease;
          }

          .modal-header {
            background: #3b82f6;
            padding: 1.25rem 1.5rem;
            color: white;
            position: relative;
          }

          .modal-header h3 {
            font-size: 1rem;
            font-weight: 600;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .student-count {
            font-size: 0.875rem;
            opacity: 0.9;
            margin-left: 0.75rem;
          }

          .modal-close {
            position: absolute;
            top: 1.25rem;
            right: 1.5rem;
            background: rgba(255, 255, 255, 0.2);
            border: none;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            color: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            transition: all 0.2s ease;
          }

          .modal-close:hover {
            background: rgba(255, 255, 255, 0.3);
          }

          .modal-content {
            padding: 1.5rem;
            max-height: 60vh;
            overflow-y: auto;
          }

          .modal-table-container {
            overflow-x: auto;
          }

          .modal-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.875rem;
          }

          .modal-table thead {
            background: #f9fafb;
          }

          .modal-table th {
            padding: 0.75rem 1rem;
            text-align: left;
            font-weight: 600;
            color: #374151;
            border-bottom: 1px solid #e5e7eb;
          }

          .modal-table td {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid #f3f4f6;
          }

          .modal-index {
            font-weight: 500;
            color: #1f2937;
            text-align: center;
          }

          .modal-name {
            font-weight: 500;
            color: #1f2937;
          }

          .modal-id {
            text-align: center;
            color: #6b7280;
          }

          .modal-correct {
            text-align: center;
            color: #10b981;
            font-weight: 600;
          }

          .modal-wrong {
            text-align: center;
            color: #ef4444;
            font-weight: 600;
          }

          .modal-marks {
            text-align: center;
            color: #3b82f6;
            font-weight: 600;
          }

          .modal-view-button {
            padding: 0.375rem 0.75rem;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.75rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .modal-view-button:hover {
            background: #2563eb;
          }

          /* Animations */
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes modalSlideUp {
            from { transform: translateY(50px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          /* Color Utilities */
          .text-blue-600 { color: #2563eb; }
          .text-green-600 { color: #16a34a; }
          .text-purple-600 { color: #7c3aed; }
          .text-amber-600 { color: #d97706; }
          .text-red-600 { color: #dc2626; }
          .text-gray-600 { color: #4b5563; }

          .bg-blue-100 { background-color: #dbeafe; color: #1d4ed8; }
          .bg-green-100 { background-color: #dcfce7; color: #16a34a; }
          .bg-purple-100 { background-color: #f3e8ff; color: #7c3aed; }
          .bg-amber-100 { background-color: #fef3c7; color: #d97706; }
          .bg-red-100 { background-color: #fee2e2; color: #dc2626; }
          .bg-gray-100 { background-color: #f3f4f6; color: #4b5563; }

          .bg-blue-50 { background-color: #eff6ff; }
          .bg-green-50 { background-color: #f0fdf4; }
          .bg-red-50 { background-color: #fef2f2; }
          .bg-gray-50 { background-color: #f9fafb; }

          /* Responsive Design */
          @media (max-width: 1024px) {
            .content-grid {
              grid-template-columns: 1fr;
            }
            
            .left-column, .right-column {
              width: 100%;
            }
          }

          @media (max-width: 768px) {
            .dashboard-container {
              padding: 1rem;
            }
            
            .summary-grid {
              grid-template-columns: 1fr;
            }
            
            .performance-stats {
              grid-template-columns: 1fr;
            }
            
            .marks-stats {
              grid-template-columns: 1fr;
            }
            
            .time-stats-grid {
              grid-template-columns: 1fr;
            }
            
            .modal-container {
              width: 95%;
              margin: 1rem;
            }
          }

          @media (max-width: 480px) {
            .tab-buttons {
              flex-direction: column;
              width: 100%;
            }
            
            .tab-button {
              width: 100%;
              text-align: center;
            }
            
            .performance-table {
              font-size: 0.75rem;
            }
            
            .performance-table th,
            .performance-table td {
              padding: 0.5rem;
            }
            
            .modal-table {
              font-size: 0.75rem;
            }
            
            .modal-table th,
            .modal-table td {
              padding: 0.5rem;
            }

            
          }

          /* Fix for table responsiveness on smaller screens */
.performance-table {
  min-width: 500px; /* Prevent table from getting too narrow */
}

.performance-table th, 
.performance-table td {
  white-space: nowrap; /* Prevent text wrapping in table cells */
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px; /* Set a max width for cells */
}

/* Make the performance table container horizontally scrollable on small screens */
.performance-table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

/* Adjust column widths for better mobile display */
@media (max-width: 768px) {
  .performance-table th:nth-child(1), /* Rank */
  .performance-table td:nth-child(1) {
    min-width: 60px;
    max-width: 60px;
  }
  
  .performance-table th:nth-child(2), /* Student Name */
  .performance-table td:nth-child(2) {
    min-width: 120px;
    max-width: 120px;
  }
  
  .performance-table th:nth-child(3), /* Accuracy */
  .performance-table td:nth-child(3) {
    min-width: 80px;
    max-width: 80px;
  }
  
  .performance-table th:nth-child(4), /* Marks */
  .performance-table td:nth-child(4) {
    min-width: 70px;
    max-width: 70px;
  }
  
  .performance-table th:nth-child(5), /* Action */
  .performance-table td:nth-child(5) {
    min-width: 70px;
    max-width: 70px;
  }
  
  /* Adjust font sizes for better readability */
  .performance-table {
    font-size: 0.8rem;
  }
  
  .accuracy-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
    min-width: 3rem;
  }
  
  .view-button {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
  }
}

/* For very small screens */
@media (max-width: 480px) {
  .performance-table {
    min-width: 400px; /* Even smaller minimum width */
  }
  
  .performance-table th:nth-child(2),
  .performance-table td:nth-child(2) {
    min-width: 100px;
    max-width: 100px;
  }
  
  .tab-buttons {
    flex-wrap: wrap;
  }
  
  .tab-button {
    flex: 1;
    min-width: 120px;
  }
}

/* Fix for marks distribution table */
.marks-table {
  min-width: 300px;
}

@media (max-width: 768px) {
  .marks-table {
    font-size: 0.8rem;
  }
  
  .marks-table th,
  .marks-table td {
    padding: 0.5rem;
  }
  
  .distribution-info {
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-start;
  }
  
  .distribution-bar {
    width: 100%;
  }
}
        `}
      </style>
    </div>
  );
};

export default AdminDashboard;